
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export function useLinkedInConnection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query to check if user has a LinkedIn connection
  const {
    data: connection,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['linkedinConnection', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      // Check for LinkedIn tokens in the database
      const { data, error } = await supabase
        .from('linkedin_oauth_tokens')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      
      // If no data or token is expired, return null
      if (!data) return null;
      
      // Check if token is expired
      const expiresAt = new Date(data.expires_at);
      const now = new Date();
      const isExpired = expiresAt <= now;
      
      // If token is expired, try to refresh
      if (isExpired && data.refresh_token) {
        try {
          const refreshResult = await refreshToken(data.refresh_token);
          if (refreshResult) {
            return refreshResult;
          }
          return null;
        } catch (e) {
          console.error('Error refreshing LinkedIn token:', e);
          return null;
        }
      }
      
      return data;
    },
    enabled: !!user,
    refetchOnWindowFocus: false,
  });

  // Function to refresh an expired token
  const refreshToken = async (refreshToken: string) => {
    // Call the LinkedIn OAuth token endpoint to refresh the token
    const { data, error } = await supabase.functions.invoke('linkedin-oauth-refresh', {
      body: { refreshToken },
    });

    if (error) {
      throw error;
    }

    return data;
  };

  // Disconnect LinkedIn account
  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');
      
      const { error } = await supabase
        .from('linkedin_oauth_tokens')
        .delete()
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['linkedinConnection', user?.id],
      });
      
      toast({
        title: "LinkedIn disconnected",
        description: "Your LinkedIn account has been disconnected successfully.",
      });
    },
    onError: (error) => {
      console.error('Error disconnecting LinkedIn:', error);
      toast({
        title: "Disconnection failed",
        description: "Could not disconnect your LinkedIn account. Please try again.",
        variant: "destructive",
      });
    },
  });

  return {
    connection,
    isConnected: !!connection,
    isLoading,
    isError,
    isDisconnecting,
    disconnect,
    refreshStatus: refetch,
  };
}
